import Email from "@/domain/entities/emails/Email";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import useGetStringifiedDateAndHour from "@/hooks/utils/useGetStringifiedDate";
import { cn } from "@/lib/utils";
import { emailAttachmentToDisplay } from "@/mappers/email/displayAttachmentMapper";
import { MoreHorizontal } from "lucide-react";
import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CategoriesList from "../mailsList/CategoriesList";
import GroupAvatars from "../mailsList/GroupAvatars";
import { Button } from "../ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";
import AttachmentChip from "../widgets/attachements/AttachmentChip";
import getAttachmentIcon from "../widgets/attachements/attachmentIcon";
import DocumentPreview from "../widgets/attachements/DocumentPreview";
import MailIframe from "./MailIframe";

type MailContentWithSenderAndDateProps = {
    mail: Email;
    isSelected?: boolean;
    isSender?: boolean;
    showFullMessage?: boolean;
    /** How the selected mail stands out (the assistant's side panel: a light border). */
    selectedClassName?: string;
};

const MailContentWithSenderAndDate = forwardRef<
    HTMLDivElement,
    MailContentWithSenderAndDateProps
>(
    (
        {
            mail,
            isSelected = false,
            isSender = false,
            showFullMessage = false,
            selectedClassName = "border-2 border-surface-calendarEventDark",
        },
        ref,
    ) => {
        const getStringifiedDate = useGetStringifiedDateAndHour();
        const [showUniqueBody, setShowUniqueBody] = useState(!showFullMessage);
        const [selectedAttachment, setSelectedAttachment] =
            useState<EmailAttachment | null>(null);
        const [selectedIndex, setSelectedIndex] = useState(0);
        const { t } = useTranslation();

        const content =
            showUniqueBody && mail.uniqueBody ? mail.uniqueBody : mail.message;
        const contentType =
            showUniqueBody && mail.uniqueBody
                ? mail.uniqueBodyContentType
                : mail.bodyContentType;

        const sentDateTimeStringified = getStringifiedDate(
            mail.sentDateTime,
            null,
            false,
        );

        const fileAttachments = mail.attachments.filter(
            (attachment) => !attachment.isEmbeddedInBody(),
        );

        return (
            <div
                ref={ref}
                className={cn(
                    "flex h-fit w-full flex-col gap-1 font-body font-medium",
                    isSender && "items-end",
                )}
            >
                <div
                    className={cn(
                        "flex w-full items-center justify-between gap-2 px-3",
                        isSender && "flex-row-reverse",
                    )}
                >
                    <div className="flex items-center gap-2">
                        <p className="text-xs text-text-caption">
                            {sentDateTimeStringified}
                        </p>
                        <GroupAvatars mail={mail} />
                    </div>
                    <CategoriesList mail={mail} />
                </div>
                <div
                    className={cn(
                        "h-fit w-full rounded-2xl p-5 text-left text-xs text-text-mainTitle",
                        isSender ? "bg-neutral-lighter" : "bg-surface-primary",
                        isSelected && selectedClassName,
                    )}
                >
                    <MailIframe
                        className="w-full"
                        content={content}
                        contentType={contentType}
                    />

                    {showUniqueBody && (
                        <TooltipProvider delayDuration={0}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        className="bg-icon-base hover:bg-primary-dark"
                                        onClick={() =>
                                            setShowUniqueBody(!showUniqueBody)
                                        }
                                        size="tinyCircle"
                                        variant="ghost"
                                    >
                                        <MoreHorizontal
                                            className="h-4 w-4"
                                            color="white"
                                        />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {t("emails.contentToggle.showOriginal")}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}

                    {fileAttachments.length > 0 && (
                        <div className="mt-4 border-t border-neutral-light pt-3">
                            <p className="mb-1.5 text-xs font-semibold text-text-caption">
                                {t("emails.attachment.title")}
                            </p>
                            <div className="flex flex-wrap items-center gap-1">
                                {fileAttachments.map((attachment, index) => (
                                    <AttachmentChip
                                        key={attachment.id}
                                        attachment={attachment}
                                        onClick={() => {
                                            setSelectedAttachment(attachment);
                                            setSelectedIndex(index);
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                {selectedAttachment && (
                    <DocumentPreview
                        attachment={emailAttachmentToDisplay(
                            selectedAttachment,
                        )}
                        hasNext={selectedIndex < fileAttachments.length - 1}
                        hasPrevious={selectedIndex > 0}
                        icon={getAttachmentIcon(selectedAttachment)}
                        isOpen={!!selectedAttachment}
                        onClose={() => setSelectedAttachment(null)}
                        onNext={() => {
                            const next = selectedIndex + 1;
                            if (next < fileAttachments.length) {
                                setSelectedIndex(next);
                                setSelectedAttachment(fileAttachments[next]);
                            }
                        }}
                        onPrevious={() => {
                            const prev = selectedIndex - 1;
                            if (prev >= 0) {
                                setSelectedIndex(prev);
                                setSelectedAttachment(fileAttachments[prev]);
                            }
                        }}
                    />
                )}
            </div>
        );
    },
);

export default MailContentWithSenderAndDate;
